import React from 'react'
import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'

import { BudgetChartBudgets } from 'src/types'

import {
  StyledDiv,
  StyledLine,
  StyledTitle,
  StyledViolatedText
} from './styles'

interface Bar {
  key: number
  backgroundColor: string
  rate: number
  title: string
}

interface BarProps {
  bars: BudgetChartBudgets
}

enum BarColor { //
  ISSUED = '#ff0000',
  CURRENT_ISSUED = '#9966ff',
  RESERVED = '#1890ff', // #1c5a9f
  CURRENT_RESERVED = '#faa63f',
  FREE = '#41b53a' // '#e4d444'
}

function formatBarSize(size: number): number {
  return size < 1 ? 1 : size
}

function formatTitle(title: string, bar: number): string {
  return `${title} ${bar.toFixed(1)}%`
}

/**
 * @param bars array containing the lines in the sequence that will be displayed
 * the bar: { backgroundColor, rate, title}
 * @returns line chart
 */
function BudgetChart({ bars }: BarProps) {
  const { t } = useTranslation()

  const {
    action,
    budget_violated,
    current_issued_budget = 0,
    current_reserved_budget = 0,
    free_budget = 0,
    issued_budget = 0,
    reserved_budget = 0
  } = bars

  const currentIssued = t('common.budget.bar-type.CURRENT_ISSUED')
  const currentReserved = t('common.budget.bar-type.CURRENT_RESERVED')
  const free = t('common.budget.bar-type.FREE')
  const issued = t('common.budget.bar-type.ISSUED')
  const reserved = t('common.budget.bar-type.RESERVED')
  const violated = t('common.budget.violated', {
    text: t(action === 'ALERT' ? 'common.budget.alert' : 'common.budget.block')
  })

  const infoBars: Bar[] = budget_violated
    ? [
        {
          backgroundColor: BarColor.ISSUED,
          key: 0,
          rate: 100,
          title: ''
        }
      ]
    : [
        {
          backgroundColor: BarColor.ISSUED,
          key: 1,
          rate: issued_budget,
          title: formatTitle(issued, issued_budget)
        },
        {
          backgroundColor: BarColor.CURRENT_ISSUED,
          key: 2,
          rate: current_issued_budget,
          title: formatTitle(currentIssued, current_issued_budget)
        },
        {
          backgroundColor: BarColor.RESERVED,
          key: 3,
          rate: reserved_budget,
          title: formatTitle(reserved, reserved_budget)
        },
        {
          backgroundColor: BarColor.CURRENT_RESERVED,
          key: 4,
          rate: current_reserved_budget,
          title: formatTitle(currentReserved, current_reserved_budget)
        },
        {
          backgroundColor: BarColor.FREE,
          key: 5,
          rate: free_budget,
          title: formatTitle(free, free_budget)
        }
      ]

  return (
    <>
      <StyledTitle>
        <span>{bars.title}</span>
      </StyledTitle>
      <StyledDiv>
        {infoBars.map(({ backgroundColor, key, rate, title }, index, arr) => (
          <Tooltip key={key} title={title}>
            <StyledLine
              backgroundColor={backgroundColor}
              isEnd={Number(index === arr.length - 1)}
              isStart={Number(!index)}
              rate={formatBarSize(rate)}
              title={title}
            />
          </Tooltip>
        ))}
      </StyledDiv>
      {budget_violated && <StyledViolatedText>{violated}</StyledViolatedText>}
    </>
  )
}

export default BudgetChart
